import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, ChevronLeft, ChevronRight } from 'lucide-react';
import ProjectCard from './ProjectCard';
import { allProjects, featuredIds } from '../data/projects';
import type { Project } from '../types/Project';

interface ProjectsProps {
  showHeading?: boolean;
}

const Projects = ({ showHeading = true }: ProjectsProps) => {
  const navigate = useNavigate();
  const [featuredIndex, setFeaturedIndex] = useState(0);

  const handleProjectsClick = () => {
    navigate('/projects');
  };

  const featuredProjects = featuredIds
    .map(id => allProjects.find(p => p.id === id))
    .filter((p): p is Project => p !== undefined);

  const nextProject = () => {
    setFeaturedIndex((prev) => (prev + 1) % featuredProjects.length);
  };

  const prevProject = () => {
    setFeaturedIndex((prev) => (prev - 1 + featuredProjects.length) % featuredProjects.length);
  };

  // More Projects: everything not already featured
  const featuredIdSet = new Set(featuredIds);
  const moreProjects = allProjects.filter(p => !featuredIdSet.has(p.id));

  return (
    <div>
      {(showHeading || featuredProjects.length > 1) && (
        <div className={`flex flex-wrap items-center gap-y-2 mb-4 lg:mb-5 ${showHeading ? 'justify-between' : 'justify-end'}`}>
          {showHeading && (
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold font-mono text-dev-text">
              <span className="text-dev-accent">//</span> Featured Projects
            </h2>
          )}

          {/* Carousel controls — small and quiet, matching the tag chips,
              so they read as a utility next to the heading rather than a
              second focal point. */}
          {featuredProjects.length > 1 && (
            <div className="flex items-center gap-2">
              <button
                onClick={prevProject}
                className="p-1.5 rounded-md border border-dev-border text-dev-text-dimmed hover:border-dev-accent hover:text-dev-accent transition-all"
                aria-label="Previous project"
              >
                <ChevronLeft size={16} />
              </button>

              <div className="flex gap-1.5">
                {featuredProjects.map((_, index) => (
                  <button
                    key={index}
                    onClick={() => setFeaturedIndex(index)}
                    className={`h-1.5 rounded-full transition-all ${
                      index === featuredIndex
                        ? 'bg-dev-accent w-5'
                        : 'bg-dev-border hover:bg-dev-text-dimmed w-1.5'
                    }`}
                    aria-label={`Go to project ${index + 1}`}
                  />
                ))}
              </div>

              <button
                onClick={nextProject}
                className="p-1.5 rounded-md border border-dev-border text-dev-text-dimmed hover:border-dev-accent hover:text-dev-accent transition-all"
                aria-label="Next project"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          )}
        </div>
      )}

      {/* Featured Project - Large Card */}
      <div className="mb-8 lg:mb-12">
        {/* Height is no longer forced: on lg+ the image column sets the row
            height and the text column scrolls internally if it's taller. */}
        <ProjectCard project={featuredProjects[featuredIndex]} featured={true} />
      </div>

      {/* More Projects - Smaller Cards */}
      {moreProjects.length > 0 && (
        <div className="mb-6 lg:mb-8">
          <h3 className="text-xl md:text-2xl font-bold font-mono mb-4 md:mb-6 text-dev-text-muted">
            More Projects
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5 lg:gap-6">
            {moreProjects.map(project => (
              <ProjectCard key={project.id} project={project} />
            ))}
          </div>
        </div>
      )}

      {/* View All Button */}
      <button
        onClick={handleProjectsClick}
        className="dev-card group w-full p-4 md:p-5 lg:p-6 text-left animate-fade-in"
      >
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg md:text-xl font-bold font-mono mb-1.5 md:mb-2 text-dev-text group-hover:text-dev-accent transition-colors">
              View All Projects
            </h3>
            <p className="text-sm md:text-base text-dev-text-muted">
              All {allProjects.length} in one grid
            </p>
          </div>
          <ArrowRight className="text-dev-text-muted group-hover:text-dev-accent group-hover:translate-x-2 transition-all" size={24} />
        </div>
      </button>
    </div>
  );
};

export default Projects;